// Component for SideBar History
import * as React from 'react';

import { Box, Grid, Paper, Typography, TextField } from '@mui/material';
import { FormControl, Select, MenuItem } from '@mui/material';
import { theme } from '../../theme/theme';
import moment from 'moment';
import { ReactComponent as MovieDownloadedIcon } from '../../assets/MovieDownloadedIcon.svg';
import { ReactComponent as MovieMissingIcon } from '../../assets/MovieMissingIcon.svg';
import { ReactComponent as MovieDownloadingIcon } from '../../assets/MovieDownloadingIcon.svg';
import { ReactComponent as MovieQueuedIcon } from '../../assets/MovieQueuedIcon.svg';
import SearchIcon from '@mui/icons-material/Search';

// JS doesn't have interfaces
//  use this commented out definition as a reference for the props that should be accepted for this component 
// export interface SideBarContentDetailsProps {
//   cloudSystemDetails : CloudSystem
// }

const SideBarContentDetails = (props) => {

    const TYPES = {
      All: 'All', // Special type used in UI only, not in backend data
      Film: 'Film',
      Edge: 'Edge',
    }

    const STATES = {
      Downloaded: 'FilesPresent',
      FilesMissing: 'FilesMissing',
      Downloading: 'Downloading',
      Queued: 'Queued',
    }

    const [typeFilter, setTypeFilter] = React.useState('All');
    const handleTypeFilterChanged = (event) => {
      setTypeFilter(event.target.value)
    }

    const [searchText, setSearchText] = React.useState('');
    const handleSearchTextChanged = (event) => {
        setSearchText(event.target.value)
    }

    let contentEntriesToShow = props.cloudSystemDetails?.contentDetails

    // Filter based on type
    if (contentEntriesToShow && typeFilter !== TYPES.All) {
      contentEntriesToShow = contentEntriesToShow.filter(x => x.type === typeFilter)
    }

    // Filter based on search
    if (contentEntriesToShow && searchText) {
        contentEntriesToShow = contentEntriesToShow.filter(x => {
            const text = searchText.toLowerCase()
            return x.title.toLowerCase().includes(text)
        })
    }

    if (contentEntriesToShow) {
        contentEntriesToShow = contentEntriesToShow.slice().sort((x1, x2) => {
          // First sort by state, then by type, then by title
          const typeOrder = [TYPES.Edge, TYPES.Film]
          const stateOrder = [STATES.Downloading, STATES.FilesMissing, STATES.Queued, STATES.Downloaded]
          if (x1.type === x2.type && x1.state === x2.state) {
            return x1.title < x2.title ? -1 : 1
          } if (x1.type !== x2.type) {
            const i1 = typeOrder.indexOf(x1.type)
            const i2 = typeOrder.indexOf(x2.type)
            return i1 - i2
          } else {
            const i1 = stateOrder.indexOf(x1.state)
            const i2 = stateOrder.indexOf(x2.state)
            return i1 - i2
          }
        })
    }

    const downloadedCount = contentEntriesToShow?.filter(x => x.state === STATES.Downloaded)?.length ?? 0
    const totalCount = contentEntriesToShow?.length ?? 0

    const visionCatalog = props.cloudSystemDetails?.activeStatus?.visionCatalogId
    const edgeContentVersion = props.cloudSystemDetails?.activeStatus?.edgeContentVersion
    const drmDateVal = props.cloudSystemDetails?.activeStatus?.visionDRMDate;
    const drmDateDisplay = isNaN(drmDateVal) || isNaN(parseInt(drmDateVal)) ? drmDateVal : moment.unix(Number(drmDateVal)).format('MMMM Do, YYYY')
    const magazineDateVal = props.cloudSystemDetails?.activeStatus?.visionMagazineDate;
    const lastStatusUpTimeDiff = props.cloudSystemDetails?.activeStatus?.lastContentDetailsUpdateTime ? moment().from(new Date(props.cloudSystemDetails.activeStatus.lastContentDetailsUpdateTime), true) : "";

    const backgroundColors = {}
    backgroundColors[STATES.Downloaded] = 'auto'
    backgroundColors[STATES.Queued] = 'auto'
    backgroundColors[STATES.FilesMissing] = '#341C28'
    backgroundColors[STATES.Downloading] = '#62B0DC33'

    const createDetailsRow = (key, value, index) => {
        return (
            <Grid key={index} container item direction={'row'}>
                <Grid item>
                    <Typography sx={{ color: theme.palette.sidebarContent.labelText }}>
                    {key}
                    </Typography>
                </Grid>
                <Grid sx={{ marginLeft: 'auto' }}>
                    <Typography sx={{ color: theme.palette.sidebarContent.valueText }}>
                    {value !== null && value !== undefined && value !== "" ? value : "-- --"}
                    </Typography>
                </Grid>
            </Grid>
        );
    }

    const createDetailsSection = (sectionTitle, keyValuePairs) => {
        return (
            <Grid container direction={'row'} alignItems='center' sx={{ borderTop: '1 solid #707070', mb: 3 }}>
            <Grid item>
                <Typography fontWeight={700} sx={{ color: theme.palette.primary.contrastText, mb: 1 }}>
                {sectionTitle}
                </Typography>
            </Grid>
            {keyValuePairs.map((keyValuePair, index) => {return createDetailsRow(keyValuePair[0], keyValuePair[1], index)})}
            </Grid>
        );
    }

  return (
    <>
      <Box sx={{ px: 2, pb: 0, position: 'sticky', top: -24, backgroundColor: theme.palette.primary.main }}>
        {createDetailsSection("Content Details", [
            ["Vision Catalog", visionCatalog],
            ["DRM", drmDateDisplay],
            ["Magazine", magazineDateVal],
            ["Edge Content Version", edgeContentVersion],
            ["Last Status", lastStatusUpTimeDiff],
        ])}
        <Grid container direction={'row'} alignItems='center' sx={{ borderTop: '1 solid #707070', mb: 1 }}>
            <Grid item>
                <Typography fontWeight={700} sx={{ color: theme.palette.primary.contrastText }}>
                Content List
                </Typography>
            </Grid>
            <Grid item sx={{ marginLeft: 'auto' }}>
                <Typography sx={{ color: theme.palette.sidebarContent.valueText }}>
                {downloadedCount} / {totalCount}
                </Typography>
            </Grid>
            <Grid item sx={{ marginLeft: 1 }}>
              <FormControl size='small'>
                <Select
                  value={typeFilter}
                  onChange={handleTypeFilterChanged}
                  sx={{
                    color: theme.palette.sidebarContent.labelText,
                    "& .MuiOutlinedInput-notchedOutline": {
                      color: theme.palette.secondary.main, 
                      borderColor: theme.palette.secondary.main
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {color: theme.palette.sidebarContent.labelText, borderColor: theme.palette.secondary.main},
                    "&:hover .MuiOutlinedInput-notchedOutline": {color: theme.palette.sidebarContent.labelText, borderColor: theme.palette.secondary.main},
                    "& .MuiSelect-iconOutlined": {color: theme.palette.sidebarContent.labelText},
                  }}
                >
                  <MenuItem value={TYPES.All}>Show All</MenuItem>
                  <MenuItem value={TYPES.Film}>Movies</MenuItem>
                  <MenuItem value={TYPES.Edge}>Edge Content</MenuItem>
                </Select>
              </FormControl>
            </Grid>
        </Grid>
        <Grid container direction={'row'} mb={2}>
          <TextField 
            onChange={handleSearchTextChanged}
            placeholder={'Search'}
            value={searchText}
            fullWidth
            InputProps={{
              startAdornment: (
                <SearchIcon
                  sx={{ color: theme.palette.primary.contrastText }}
                />
              ),
            }}
            sx={{ 
              "& .MuiOutlinedInput-root":{"& > fieldset": {borderColor: theme.palette.secondary.main}},
              "& .MuiOutlinedInput-input.Mui-disabled":{WebkitTextFillColor: 'white'},
              input: { 
                color: theme.palette.primary.contrastText, 
              }, 
            }}
          />
        </Grid>
      </Box>
      <Paper
          elevation={0}
          sx={{
              p: 0,
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: theme.palette.primary.main
          }}
      >
        {contentEntriesToShow?.map((content, index) => {
          return (
            <Grid key={content.id} container item direction={'row'} py={0.5} px={2} sx={{ backgroundColor: backgroundColors[content.state] }}>
                <Grid item>
                    <Typography sx={{ color: content.downloaded ? theme.palette.sidebarContent.labelText : theme.palette.primary.contrastText, maxWidth: 325 }}>
                    {content.title}
                    </Typography>
                </Grid>
                {content.state === STATES.Downloaded ?
                <Grid sx={{ marginLeft: 'auto'}}>
                    <Typography sx={{ color: theme.palette.sidebarContent.labelText }}>
                    Downloaded
                    <span style={{ marginLeft: '0.5em'}}></span>
                    <MovieDownloadedIcon preserveAspectRatio={'xMidYMid meet'} height='14px' width='14px' color='#52B35B' />
                    </Typography>
                </Grid>
                : null}
                {content.state === STATES.FilesMissing ?
                <Grid sx={{ marginLeft: 'auto' }}>
                    <Typography sx={{ color: theme.palette.primary.contrastText }}>
                    Missing File
                    <span style={{ marginLeft: '0.5em'}}></span>
                    <MovieMissingIcon preserveAspectRatio={'xMidYMid meet'} height='14px' width='14px' color='#4B2634' />
                    </Typography>
                </Grid>
                : null}
                {content.state === STATES.Queued ?
                <Grid sx={{ marginLeft: 'auto' }}>
                    <Typography sx={{ color: theme.palette.sidebarContent.labelText }}>
                    Queued
                    <span style={{ marginLeft: '0.5em'}}></span>
                    <MovieQueuedIcon preserveAspectRatio={'xMidYMid meet'} height='14px' width='14px' color='#AEB1B4' />
                    </Typography>
                </Grid>
                : null}
                {content.state === STATES.Downloading ?
                <Grid sx={{ marginLeft: 'auto' }}>
                    <Typography sx={{ color: theme.palette.primary.contrastText }}>
                    Downloading
                    <span style={{ marginLeft: '0.5em'}}></span>
                    <MovieDownloadingIcon preserveAspectRatio={'xMidYMid meet'} height='14px' width='14px' color='#62B0DC' />
                    </Typography>
                </Grid>
                : null}
            </Grid>
          );
        })}
      </Paper>
    </>
  );
};

export default SideBarContentDetails;